import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  AlertTitle,
  CircularProgress,
  Card,
  CardContent,
  CardActions,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Container,
  Grid,
  Chip,
  Stack,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Autocomplete,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle,
  Error as ErrorIcon,
  Warning as WarningIcon,
  PlayArrow as TestIcon,
  Refresh as RefreshIcon,
  NotificationsActive as NotificationsActiveIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  Settings as SettingsIcon,
  ExpandMore as ExpandMoreIcon,
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { emergencyServicesApi } from '../services/apiClient';
import { useThemeSettings } from '../contexts/ThemeContext';
import { usePermissions } from '../contexts/PermissionContext';

// Tab Panel Component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`emergency-tabpanel-${index}`}
      aria-labelledby={`emergency-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const EmergencyServicesManagement = () => {
  const { t } = useThemeSettings();
  const { enqueueSnackbar } = useSnackbar();
  const { hasPermission } = usePermissions();

  // State
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [providers, setProviders] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [alarmTypes, setAlarmTypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [stats, setStats] = useState(null);

  // Dialog state
  const [integrationDialogOpen, setIntegrationDialogOpen] = useState(false);
  const [editingIntegration, setEditingIntegration] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingIntegration, setDeletingIntegration] = useState(null);
  const [testing, setTesting] = useState(null);

  // Provider dialog state
  const [providerDialogOpen, setProviderDialogOpen] = useState(false);
  const [editingProvider, setEditingProvider] = useState(null);
  const [deleteProviderDialogOpen, setDeleteProviderDialogOpen] = useState(false);
  const [deletingProvider, setDeletingProvider] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    locationId: null,
    providerId: null,
    isActive: true,
    clientReference: '',
    clientName: '',
    contractNumber: '',
    alarmCodeMapping: {},
    config: {},
    notes: ''
  });

  // Provider form state
  const [providerFormData, setProviderFormData] = useState({
    code: '',
    friendlyName: '',
    description: '',
    providerType: 'middleware',
    apiUrl: '',
    isActive: true,
    color: '#E53935',
    authType: 'basic',
    timeout: 30000
  });

  const canManage = hasPermission('devices.edit') || hasPermission('admin');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [providersRes, integrationsRes, alarmTypesRes, sitesRes, statsRes] = await Promise.all([
        emergencyServicesApi.getProviders(),
        emergencyServicesApi.getIntegrations({ limit: 1000 }),
        emergencyServicesApi.getAlarmTypes(),
        emergencyServicesApi.getSites(), // Get available sites with hasIntegration flag
        emergencyServicesApi.getStats()
      ]);

      if (providersRes.data.success) setProviders(providersRes.data.data || []);
      if (integrationsRes.data.success) setIntegrations(integrationsRes.data.data?.integrations || []);
      if (alarmTypesRes.data.success) setAlarmTypes(alarmTypesRes.data.data || []);
      if (sitesRes.data.success) setLocations(sitesRes.data.data || []);
      if (statsRes.data.success) setStats(statsRes.data.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      enqueueSnackbar('Failed to load data', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenIntegrationDialog = (integration = null) => {
    if (integration) {
      setEditingIntegration(integration);
      setFormData({
        locationId: integration.locationId,
        providerId: integration.providerId,
        isActive: integration.isActive,
        clientReference: integration.clientReference || '',
        clientName: integration.clientName || '',
        contractNumber: integration.contractNumber || '',
        alarmCodeMapping: integration.alarmCodeMapping || {},
        config: integration.config || {},
        notes: integration.notes || ''
      });
    } else {
      setEditingIntegration(null);
      setFormData({
        locationId: null,
        providerId: null,
        isActive: true,
        clientReference: '',
        clientName: '',
        contractNumber: '',
        alarmCodeMapping: {},
        config: {},
        notes: ''
      });
    }
    setIntegrationDialogOpen(true);
  };

  const handleCloseIntegrationDialog = () => {
    setIntegrationDialogOpen(false);
    setEditingIntegration(null);
  };

  const handleSaveIntegration = async () => {
    try {
      if (!formData.locationId || !formData.providerId) {
        enqueueSnackbar('Please select a location and provider', { variant: 'warning' });
        return;
      }

      if (editingIntegration) {
        await emergencyServicesApi.updateIntegration(editingIntegration.id, formData);
        enqueueSnackbar('Integration updated successfully', { variant: 'success' });
      } else {
        await emergencyServicesApi.createIntegration(formData);
        enqueueSnackbar('Integration created successfully', { variant: 'success' });
      }

      handleCloseIntegrationDialog();
      fetchData();
    } catch (error) {
      console.error('Error saving integration:', error);
      enqueueSnackbar(error.response?.data?.error || 'Failed to save integration', { variant: 'error' });
    }
  };

  const handleDeleteIntegration = async () => {
    try {
      await emergencyServicesApi.deleteIntegration(deletingIntegration.id);
      enqueueSnackbar('Integration deleted successfully', { variant: 'success' });
      setDeleteDialogOpen(false);
      setDeletingIntegration(null);
      fetchData();
    } catch (error) {
      console.error('Error deleting integration:', error);
      enqueueSnackbar(error.response?.data?.error || 'Failed to delete integration', { variant: 'error' });
    }
  };

  const handleTestIntegration = async (integration) => {
    setTesting(integration.id);
    try {
      const response = await emergencyServicesApi.testIntegration(integration.id);
      if (response.data.success) {
        enqueueSnackbar('Connection test successful', { variant: 'success' });
      } else {
        enqueueSnackbar('Connection test failed', { variant: 'error' });
      }
      fetchData();
    } catch (error) {
      console.error('Error testing integration:', error);
      enqueueSnackbar(error.response?.data?.error || 'Connection test failed', { variant: 'error' });
    } finally {
      setTesting(null);
    }
  };

  const handleAlarmCodeChange = (alarmType, value) => {
    setFormData(prev => ({
      ...prev,
      alarmCodeMapping: {
        ...prev.alarmCodeMapping,
        [alarmType]: value
      }
    }));
  };

  // Provider management functions
  const handleOpenProviderDialog = (provider = null) => {
    if (provider) {
      setEditingProvider(provider);
      setProviderFormData({
        code: provider.code || '',
        friendlyName: provider.friendlyName || '',
        description: provider.description || '',
        providerType: provider.providerType || 'middleware',
        apiUrl: provider.apiUrl || '',
        isActive: provider.isActive !== false,
        color: provider.color || '#E53935',
        authType: provider.authType || 'basic',
        timeout: provider.timeout || 30000
      });
    } else {
      setEditingProvider(null);
      setProviderFormData({
        code: '',
        friendlyName: '',
        description: '',
        providerType: 'middleware',
        apiUrl: '',
        isActive: true,
        color: '#E53935',
        authType: 'basic',
        timeout: 30000
      });
    }
    setProviderDialogOpen(true);
  };

  const handleCloseProviderDialog = () => {
    setProviderDialogOpen(false);
    setEditingProvider(null);
  };

  const handleSaveProvider = async () => {
    try {
      if (!providerFormData.code || !providerFormData.friendlyName) {
        enqueueSnackbar('Please fill in code and name', { variant: 'warning' });
        return;
      }

      if (editingProvider) {
        await emergencyServicesApi.updateProvider(editingProvider.id, providerFormData);
        enqueueSnackbar('Provider updated successfully', { variant: 'success' });
      } else {
        await emergencyServicesApi.createProvider(providerFormData);
        enqueueSnackbar('Provider created successfully', { variant: 'success' });
      }

      handleCloseProviderDialog();
      fetchData();
    } catch (error) {
      console.error('Error saving provider:', error);
      enqueueSnackbar(error.response?.data?.error || 'Failed to save provider', { variant: 'error' });
    }
  };

  const handleDeleteProvider = async () => {
    try {
      await emergencyServicesApi.deleteProvider(deletingProvider.id);
      enqueueSnackbar('Provider deleted successfully', { variant: 'success' });
      setDeleteProviderDialogOpen(false);
      setDeletingProvider(null);
      fetchData();
    } catch (error) {
      console.error('Error deleting provider:', error);
      enqueueSnackbar(error.response?.data?.error || 'Failed to delete provider', { variant: 'error' });
    }
  };

  const getProviderIcon = (provider) => {
    switch (provider.icon) {
      case 'LocalHospital':
        return <NotificationsActiveIcon sx={{ color: provider.color || 'error.main' }} />;
      default:
        return <NotificationsActiveIcon sx={{ color: provider.color || 'error.main' }} />;
    }
  };

  const getStatusChip = (integration) => {
    if (!integration.isActive) {
      return <Chip label="Inactive" size="small" color="default" />;
    }
    if (integration.lastErrorAt && (!integration.lastSuccessfulConnection ||
        new Date(integration.lastErrorAt) > new Date(integration.lastSuccessfulConnection))) {
      return <Chip label="Error" size="small" color="error" icon={<ErrorIcon />} />;
    }
    if (integration.lastSuccessfulConnection) {
      return <Chip label="Connected" size="small" color="success" icon={<CheckCircle />} />;
    }
    return <Chip label="Not Tested" size="small" color="warning" icon={<WarningIcon />} />;
  };

  // Get sites that don't have an integration yet (uses hasIntegration from API)
  const getAvailableLocations = () => {
    return locations.filter(loc =>
      !loc.hasIntegration || (editingIntegration && editingIntegration.locationId === loc.id)
    );
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading emergency service integrations...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => window.location.href = '/integration-management'}
          sx={{ mb: 2 }}
        >
          Back to Integrations
        </Button>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <NotificationsActiveIcon sx={{ mr: 2, fontSize: 40, color: 'error.main' }} />
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                Emergency Service Integrations
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Configure alarm routing to emergency service providers per site
              </Typography>
            </Box>
          </Box>

          <Button
            variant="contained"
            color="error"
            startIcon={<AddIcon />}
            onClick={() => handleOpenIntegrationDialog()}
            disabled={!canManage}
          >
            Add Integration
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>Available Providers</Typography>
                <Typography variant="h3" sx={{ fontWeight: 700, color: 'primary.main' }}>
                  {stats.totalProviders}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>Total Integrations</Typography>
                <Typography variant="h3" sx={{ fontWeight: 700, color: 'info.main' }}>
                  {stats.totalIntegrations}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>Active Integrations</Typography>
                <Typography variant="h3" sx={{ fontWeight: 700, color: 'success.main' }}>
                  {stats.activeIntegrations}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>Sites Without Integration</Typography>
                <Typography variant="h3" sx={{ fontWeight: 700, color: 'warning.main' }}>
                  {locations.length - stats.totalIntegrations}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Site Integrations" icon={<LocationIcon />} iconPosition="start" />
          <Tab label="Providers" icon={<BusinessIcon />} iconPosition="start" />
        </Tabs>

        {/* Site Integrations Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ px: 3 }}>
            {integrations.length === 0 ? (
              <Alert severity="info">
                <AlertTitle>No integrations configured</AlertTitle>
                Click "Add Integration" to configure emergency service routing for your sites.
              </Alert>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Site</TableCell>
                      <TableCell>Provider</TableCell>
                      <TableCell>Client Reference</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Last Test</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {integrations.map((integration) => (
                      <TableRow key={integration.id}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <LocationIcon sx={{ mr: 1, color: 'text.secondary' }} />
                            {integration.location?.friendlyName || `Location #${integration.locationId}`}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={integration.provider?.friendlyName || 'Unknown'}
                            size="small"
                            sx={{
                              bgcolor: integration.provider?.color ? `${integration.provider.color}20` : undefined,
                              color: integration.provider?.color
                            }}
                          />
                        </TableCell>
                        <TableCell>{integration.clientReference || '-'}</TableCell>
                        <TableCell>{getStatusChip(integration)}</TableCell>
                        <TableCell>
                          {integration.lastSuccessfulConnection
                            ? new Date(integration.lastSuccessfulConnection).toLocaleString()
                            : 'Never'}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Test Connection">
                            <IconButton
                              size="small"
                              onClick={() => handleTestIntegration(integration)}
                              disabled={testing === integration.id}
                            >
                              {testing === integration.id ? (
                                <CircularProgress size={20} />
                              ) : (
                                <TestIcon />
                              )}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenIntegrationDialog(integration)}
                              disabled={!canManage}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                setDeletingIntegration(integration);
                                setDeleteDialogOpen(true);
                              }}
                              disabled={!canManage}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </TabPanel>

        {/* Providers Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ px: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenProviderDialog()}
                disabled={!canManage}
              >
                Add Provider
              </Button>
            </Box>
            {providers.length === 0 ? (
              <Alert severity="info">
                <AlertTitle>No providers configured</AlertTitle>
                Click "Add Provider" to create a new emergency service provider, or run the database migrations to add default providers.
              </Alert>
            ) : (
              <Grid container spacing={3}>
                {providers.map((provider) => (
                  <Grid item xs={12} md={6} lg={4} key={provider.id}>
                    <Card variant="outlined">
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box
                              sx={{
                                p: 1,
                                borderRadius: 1,
                                bgcolor: provider.color ? `${provider.color}20` : 'error.light',
                                mr: 2
                              }}
                            >
                              {getProviderIcon(provider)}
                            </Box>
                            <Box>
                              <Typography variant="h6">{provider.friendlyName}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {provider.code} • {provider.providerType}
                              </Typography>
                            </Box>
                          </Box>
                          {canManage && (
                            <Box>
                              <Tooltip title="Edit">
                                <IconButton size="small" onClick={() => handleOpenProviderDialog(provider)}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => {
                                    setDeletingProvider(provider);
                                    setDeleteProviderDialogOpen(true);
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                        </Box>

                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 40 }}>
                          {provider.description || 'No description'}
                        </Typography>

                        <Divider sx={{ my: 2 }} />

                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Configuration:</Typography>
                        <Stack spacing={0.5}>
                          <Typography variant="caption">Auth: {provider.authType || 'none'}</Typography>
                          <Typography variant="caption">Format: {provider.requestFormat || 'json'}</Typography>
                          <Typography variant="caption">Timeout: {provider.timeout || 30000}ms</Typography>
                        </Stack>

                        {stats?.byProvider && (
                          <Box sx={{ mt: 2 }}>
                            <Chip
                              label={`${stats.byProvider.find(p => p.code === provider.code)?.count || 0} site(s)`}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        </TabPanel>
      </Paper>

      {/* Integration Dialog */}
      <Dialog
        open={integrationDialogOpen}
        onClose={handleCloseIntegrationDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingIntegration ? 'Edit Integration' : 'Add Integration'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {/* Location Selection */}
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={getAvailableLocations()}
                getOptionLabel={(option) => option.friendlyName || `Location #${option.id}`}
                value={locations.find(l => l.id === formData.locationId) || null}
                onChange={(e, value) => setFormData(prev => ({ ...prev, locationId: value?.id || null }))}
                disabled={!!editingIntegration}
                renderInput={(params) => (
                  <TextField {...params} label="Site / Location" required fullWidth />
                )}
              />
            </Grid>

            {/* Provider Selection */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Emergency Service Provider</InputLabel>
                <Select
                  value={formData.providerId || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, providerId: e.target.value }))}
                  label="Emergency Service Provider"
                  disabled={!!editingIntegration}
                >
                  {providers.map((provider) => (
                    <MenuItem key={provider.id} value={provider.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            bgcolor: provider.color || 'error.main',
                            mr: 1
                          }}
                        />
                        {provider.friendlyName}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Active Toggle */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                  />
                }
                label="Integration Active"
              />
            </Grid>

            <Grid item xs={12}>
              <Divider>
                <Chip label="Client Information" size="small" />
              </Divider>
            </Grid>

            {/* Client Reference */}
            <Grid item xs={12} md={4}>
              <TextField
                label="Client Reference / ID"
                fullWidth
                value={formData.clientReference}
                onChange={(e) => setFormData(prev => ({ ...prev, clientReference: e.target.value }))}
                helperText="Your ID at the provider"
              />
            </Grid>

            {/* Client Name */}
            <Grid item xs={12} md={4}>
              <TextField
                label="Client Name"
                fullWidth
                value={formData.clientName}
                onChange={(e) => setFormData(prev => ({ ...prev, clientName: e.target.value }))}
                helperText="How the provider knows you"
              />
            </Grid>

            {/* Contract Number */}
            <Grid item xs={12} md={4}>
              <TextField
                label="Contract Number"
                fullWidth
                value={formData.contractNumber}
                onChange={(e) => setFormData(prev => ({ ...prev, contractNumber: e.target.value }))}
              />
            </Grid>

            {/* Provider Configuration - shows based on selected provider */}
            {formData.providerId && (() => {
              const selectedProvider = providers.find(p => p.id === formData.providerId);
              if (!selectedProvider) return null;

              // Email Only provider configuration
              if (selectedProvider.code === 'email_only') {
                return (
                  <Grid item xs={12}>
                    <Accordion defaultExpanded>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle1">Email Configuration</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <TextField
                              label="Recipient Emails"
                              fullWidth
                              required
                              value={formData.config?.recipientEmails?.join(', ') || ''}
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                config: {
                                  ...prev.config,
                                  recipientEmails: e.target.value.split(',').map(email => email.trim()).filter(Boolean)
                                }
                              }))}
                              placeholder="email1@example.com, email2@example.com"
                              helperText="Comma-separated list of email addresses to receive alarm notifications"
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              label="CC Emails"
                              fullWidth
                              value={formData.config?.ccEmails?.join(', ') || ''}
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                config: {
                                  ...prev.config,
                                  ccEmails: e.target.value.split(',').map(email => email.trim()).filter(Boolean)
                                }
                              }))}
                              placeholder="cc1@example.com, cc2@example.com"
                              helperText="Optional CC addresses"
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              label="Subject Prefix"
                              fullWidth
                              value={formData.config?.subjectPrefix || '[ALARM]'}
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                config: {
                                  ...prev.config,
                                  subjectPrefix: e.target.value
                                }
                              }))}
                              helperText="Prefix for email subject line"
                            />
                          </Grid>
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                );
              }

              // IQmessenger configuration
              if (selectedProvider.code === 'iqmessenger') {
                return (
                  <Grid item xs={12}>
                    <Accordion defaultExpanded>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle1">IQmessenger Configuration</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <TextField
                              label="Client ID"
                              fullWidth
                              required
                              value={formData.config?.clientId || ''}
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                config: { ...prev.config, clientId: e.target.value }
                              }))}
                              helperText="Your IQmessenger client ID"
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              label="API Key"
                              fullWidth
                              required
                              type="password"
                              value={formData.config?.apiKey || ''}
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                config: { ...prev.config, apiKey: e.target.value }
                              }))}
                              helperText="Your IQmessenger API key"
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              label="Endpoint URL"
                              fullWidth
                              value={formData.config?.endpoint || ''}
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                config: { ...prev.config, endpoint: e.target.value }
                              }))}
                              placeholder="https://api.iqmessenger.com/v1/alarms"
                              helperText="IQmessenger API endpoint (optional, uses default if empty)"
                            />
                          </Grid>
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                );
              }

              // Generic provider - show raw config editor
              return (
                <Grid item xs={12}>
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="subtitle1">Provider Configuration</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <TextField
                        label="Configuration (JSON)"
                        fullWidth
                        multiline
                        rows={4}
                        value={JSON.stringify(formData.config || {}, null, 2)}
                        onChange={(e) => {
                          try {
                            const parsed = JSON.parse(e.target.value);
                            setFormData(prev => ({ ...prev, config: parsed }));
                          } catch (err) {
                            // Invalid JSON, don't update
                          }
                        }}
                        helperText="Provider-specific configuration in JSON format"
                      />
                    </AccordionDetails>
                  </Accordion>
                </Grid>
              );
            })()}

            {/* Alarm Code Mapping */}
            <Grid item xs={12}>
              <Accordion defaultExpanded={Object.keys(formData.alarmCodeMapping).length > 0}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">Alarm Code Mapping</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Map CARO alarm types to provider-specific alarm codes
                  </Typography>
                  {alarmTypes.length === 0 ? (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      No alarm types configured in the system yet. Run the database migrations to add default alarm types.
                    </Alert>
                  ) : (
                    <Grid container spacing={2}>
                      {alarmTypes.map((alarmType) => (
                        <Grid item xs={12} sm={6} md={4} key={alarmType.id}>
                          <TextField
                            label={alarmType.friendlyName}
                            fullWidth
                            size="small"
                            value={formData.alarmCodeMapping[alarmType.buttonType] || ''}
                            onChange={(e) => handleAlarmCodeChange(alarmType.buttonType, e.target.value)}
                            placeholder="Provider alarm code"
                            helperText={alarmType.buttonType}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </AccordionDetails>
              </Accordion>
            </Grid>

            {/* Notes */}
            <Grid item xs={12}>
              <TextField
                label="Notes"
                fullWidth
                multiline
                rows={3}
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleCloseIntegrationDialog}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<SaveIcon />}
            onClick={handleSaveIntegration}
          >
            {editingIntegration ? 'Update' : 'Create'} Integration
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Integration</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the integration for{' '}
            <strong>{deletingIntegration?.location?.friendlyName}</strong>?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            This will stop routing alarms from this site to the emergency service provider.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteIntegration}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Provider Dialog */}
      <Dialog
        open={providerDialogOpen}
        onClose={handleCloseProviderDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingProvider ? 'Edit Provider' : 'Add Provider'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Code"
                fullWidth
                required
                value={providerFormData.code}
                onChange={(e) => setProviderFormData(prev => ({ ...prev, code: e.target.value }))}
                helperText="Unique identifier (e.g., 'iqmessenger')"
                disabled={!!editingProvider}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Name"
                fullWidth
                required
                value={providerFormData.friendlyName}
                onChange={(e) => setProviderFormData(prev => ({ ...prev, friendlyName: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={2}
                value={providerFormData.description}
                onChange={(e) => setProviderFormData(prev => ({ ...prev, description: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Provider Type</InputLabel>
                <Select
                  value={providerFormData.providerType}
                  onChange={(e) => setProviderFormData(prev => ({ ...prev, providerType: e.target.value }))}
                  label="Provider Type"
                >
                  <MenuItem value="middleware">Middleware</MenuItem>
                  <MenuItem value="direct">Direct</MenuItem>
                  <MenuItem value="email">Email Only</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Authentication Type</InputLabel>
                <Select
                  value={providerFormData.authType}
                  onChange={(e) => setProviderFormData(prev => ({ ...prev, authType: e.target.value }))}
                  label="Authentication Type"
                >
                  <MenuItem value="none">None</MenuItem>
                  <MenuItem value="basic">Basic Auth</MenuItem>
                  <MenuItem value="bearer">Bearer Token</MenuItem>
                  <MenuItem value="api_key">API Key</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="API URL"
                fullWidth
                value={providerFormData.apiUrl}
                onChange={(e) => setProviderFormData(prev => ({ ...prev, apiUrl: e.target.value }))}
                placeholder="https://api.provider.com/alarms"
                helperText="Base URL for the provider's API (can be set per integration)"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Color"
                fullWidth
                type="color"
                value={providerFormData.color}
                onChange={(e) => setProviderFormData(prev => ({ ...prev, color: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Timeout (ms)"
                fullWidth
                type="number"
                value={providerFormData.timeout}
                onChange={(e) => setProviderFormData(prev => ({ ...prev, timeout: parseInt(e.target.value) || 30000 }))}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={providerFormData.isActive}
                    onChange={(e) => setProviderFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                  />
                }
                label="Provider Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleCloseProviderDialog}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSaveProvider}
          >
            {editingProvider ? 'Update' : 'Create'} Provider
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Provider Confirmation Dialog */}
      <Dialog open={deleteProviderDialogOpen} onClose={() => setDeleteProviderDialogOpen(false)}>
        <DialogTitle>Delete Provider</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the provider{' '}
            <strong>{deletingProvider?.friendlyName}</strong>?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            This provider cannot be deleted if it has any active integrations.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteProviderDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteProvider}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default EmergencyServicesManagement;
